import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useHeader, useHeaderLinks } from 'providers/AreasProvider';
import useDebounce from 'utils/hooks/useDebounce';
import { Grid, Box } from '@mui/material';
import { FilterList, GridOn, ArrowBack, Print } from '@mui/icons-material';
import { Card, Button, Autocomplete, IconButton, Typography } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import BulkPrintingListFilters from './BulkPrintingListFilters';
import BulkPrintingList from './BulkPrintingList';
import documentTypeIds from 'constants/documentTypeIds';
import { BULK_PRINTING_OFFERS, BULK_DOCUMENT_TEMPLATES } from '../queries';
import { DOCUMENT_STATES } from 'features/common/queries/DictionaryQueries';
import { BULK_PRINT_OFFERS } from '../mutations';
import { BULK_PRINT_OFFERS_EXECUTED } from '../subscriptions';
import { useQueryWithErrorHandling } from 'customHooks/errorHandling';
import { useMutation, useSubscription } from '@apollo/client';
import { showError, showInfo } from 'actions/actionCreators/snackbarActions';
import { buildDownloadUrl } from '../../../../document/edit/documents/actionCreators';
import WrappedFakeText from 'components/common/WrappedFakeText';

const defaultFilters = {
  fullTextFilter: '',
  offerNumber: '',
  offerStateId: null,
  offerStartDate: null,
  offerEndDate: null
};

const BulkPrintingListContainer = ({ filters, onFiltersChanged, pager, onPageChange, onRowsPerPageChange }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  useHeader(
    <Typography variant='subtitle1' fontWeight='bold'>
      {t('BulkPrinting.Title')}
    </Typography>
  );
  const [, setHeaderLinks] = useHeaderLinks();

  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedOfferList, setSelectedOfferList] = useState([]);
  const [checkAllOffers, setCheckAllOffers] = useState(false);

  const [saving, setSaving] = useState(false);

  const [editFilters, setEditFilters] = useState(defaultFilters);
  const debouncedEditFilters = useDebounce(editFilters, 500);

  const { loading: loadingBulkPrintingOffers, data: bulkPrintingOffers } = useQueryWithErrorHandling(BULK_PRINTING_OFFERS, {
    variables: {
      pager: {
        size: pager.pageSize,
        page: pager.page
      },
      filters: filters
    }
  });

  const { loading: loadingDocumentTemplates, data: documentTemplates } = useQueryWithErrorHandling(BULK_DOCUMENT_TEMPLATES, {});

  const { loading: loadingDocumentStates, data: documentStates } = useQueryWithErrorHandling(DOCUMENT_STATES, {
    variables: {
      documentTypeId: documentTypeIds.Offer
    }
  });

  const [bulkPrintOffers] = useMutation(BULK_PRINT_OFFERS, {
    onError: error => dispatch(showError(error))
  });

  useSubscription(BULK_PRINT_OFFERS_EXECUTED, {
    onSubscriptionData: ({
      subscriptionData: {
        data: { bulkPrintOffersExecuted }
      }
    }) => {
      const url = buildDownloadUrl(
        bulkPrintOffersExecuted.documentId,
        bulkPrintOffersExecuted.siteId,
        bulkPrintOffersExecuted.file.filePath,
        bulkPrintOffersExecuted.file.fileName
      )();
      window.location = url;
      setSaving(false);
      dispatch(showInfo(t('BulkPrinting.BulkPrintOffersExecuted'), 'success'));
    }
  });

  const handleBackToList = useCallback(() => {
    history.push('/offers');
  }, [history]);

  const handleChangeFilterValue = useCallback(
    prop => value => {
      setEditFilters({ ...editFilters, [prop]: value });
    },
    [editFilters]
  );

  const handleSelectedTemplateChange = useCallback(value => {
    setSelectedTemplateId(value || null);
  }, []);

  const handleSelectOffer = useCallback(
    item => event => {
      const value = event.target.checked;
      if (value === true) {
        setSelectedOfferList([...selectedOfferList, { offerId: item.offerId, offerSiteId: item.offerSiteId }]);
      } else if (value === false) {
        let newList = [...selectedOfferList];
        newList = newList.filter(x => x.offerId !== item.offerId);
        setSelectedOfferList(newList);
      }
    },
    [selectedOfferList]
  );

  const handleCheckAll = () => {
    if (!checkAllOffers) handleSelectAllOffers();
    else handleDeselectAllOffers();
  };

  const handleSelectAllOffers = useCallback(() => {
    const offersFromCurrentPage = loadingBulkPrintingOffers === false ? bulkPrintingOffers.pagedBulkPrintingOffers.values : [];
    const offerList =
      offersFromCurrentPage.length > 0
        ? offersFromCurrentPage.map(offer => ({
            offerId: offer.offerId,
            offerSiteId: offer.offerSiteId
          }))
        : [];
    setSelectedOfferList([...selectedOfferList, ...offerList]);
    setCheckAllOffers(true);
  }, [loadingBulkPrintingOffers, bulkPrintingOffers, selectedOfferList]);

  const handleDeselectAllOffers = useCallback(() => {
    setSelectedOfferList([]);
    setCheckAllOffers(false);
  }, []);

  const handleRefresh = useCallback(() => {
    onFiltersChanged(editFilters);
  }, [editFilters, onFiltersChanged]);

  const handleFilterReset = useCallback(() => {
    setEditFilters(defaultFilters);
  }, []);

  const handleBulkPrintOffers = useCallback(() => {
    setSaving(true);
    bulkPrintOffers({ variables: { input: { documentTemplateId: selectedTemplateId, offerList: selectedOfferList } } });
    setSelectedTemplateId(null);
    setSelectedOfferList([]);
    setCheckAllOffers(false);
  }, [bulkPrintOffers, selectedOfferList, selectedTemplateId]);

  useEffect(() => {
    setHeaderLinks(
      <Box px={1}>
        <IconButton
          id='bulkPrinting_backToListBtn'
          tooltip={t('OfferAction.BackToList')}
          color='primary'
          variant='text'
          onClick={handleBackToList}
        >
          <ArrowBack color='inherit' />
        </IconButton>
      </Box>
    );
  }, [t, setHeaderLinks, handleBackToList]);

  useEffect(() => {
    onFiltersChanged(editFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEditFilters]);

  return (
    <>
      <Card icon={FilterList} title={t('BulkPrinting.FiltersTitle')}>
        <BulkPrintingListFilters
          documentStates={loadingDocumentStates === false ? documentStates?.documentStates : []}
          filters={editFilters}
          onChangeFilterValue={handleChangeFilterValue}
          onFilterReset={handleFilterReset}
        />
      </Card>
      <Card icon={GridOn}>
        <>
          <Grid item container justifyContent='space-between' alignItems='center'>
            <Grid item flexGrow={1} maxWidth='500px'>
              <Autocomplete
                id={'bulkPrinting_documentTemplate'}
                fullWidth
                valueKey='documentTemplateId'
                value={selectedTemplateId}
                onChange={handleSelectedTemplateChange}
                labelKey='templateName'
                label={t('BulkPrinting.DocumentTemplate')}
                options={loadingDocumentTemplates === false ? documentTemplates?.bulkDocumentTemplates : []}
                isSearchable
                isClearable
                simpleValue
              />
            </Grid>
            <Grid item>
              <Button
                id={'bulkPrinting_bulkPrintBtn'}
                onClick={handleBulkPrintOffers}
                disabled={selectedTemplateId && selectedOfferList.length > 0 && !saving ? false : true}
              >
                <Print />
                {saving ? t('BulkPrinting.Printing') : t('BulkPrinting.Print')}
              </Button>
            </Grid>
          </Grid>
          {loadingBulkPrintingOffers ? (
            <WrappedFakeText my={2} />
          ) : (
            bulkPrintingOffers?.pagedBulkPrintingOffers.values.length > 0 && (
              <BulkPrintingList
                bulkPrintingOffers={bulkPrintingOffers?.pagedBulkPrintingOffers?.values}
                onSelectOffer={handleSelectOffer}
                handleCheckAll={handleCheckAll}
                checkAllOffers={checkAllOffers}
                selectedOfferList={selectedOfferList}
              />
            )
          )}
          <PaginationWrapper
            count={bulkPrintingOffers?.pagedBulkPrintingOffers.pagination.totalCount || 0}
            pageSize={pager.pageSize}
            page={pager.page}
            onRowsPerPageChange={onRowsPerPageChange}
            onPageChange={onPageChange}
            onRefresh={handleRefresh}
          />
        </>
      </Card>
    </>
  );
};

BulkPrintingListContainer.propTypes = {
  pager: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  onFiltersChanged: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired
};

const BulkPrintingListContainerWrapper = () => {
  const [pager, setPager] = useState({ page: 1, pageSize: 10 });
  const [filters, setFilters] = useState(defaultFilters);

  const pagerUpdater = useCallback(
    (prop, value) => {
      return prop === 'pageSize' ? { ...pager, [prop]: value, page: 1 } : { ...pager, [prop]: value };
    },
    [pager]
  );

  const onPageChange = page => {
    setPager(pagerUpdater('page', ++page));
  };

  const onRowsPerPageChange = pageSize => {
    setPager(pagerUpdater('pageSize', pageSize));
  };

  const handleFiltersChanged = useCallback(
    filters => {
      setFilters({ ...filters });
      setPager(pagerUpdater('page', 1));
    },
    [pagerUpdater]
  );

  return (
    <BulkPrintingListContainer
      filters={filters}
      pager={pager}
      onFiltersChanged={handleFiltersChanged}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
    />
  );
};

export default BulkPrintingListContainerWrapper;
